import { useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Stack } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { useDebounce } from '@uidotdev/usehooks';
import i18n from 'i18next';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { DialogStyled, RelativelyPositionedSearchFilter, Table } from '@/components';
import { DEBOUNCE_TIME_MS } from '@/constants';
import {
  findRowById,
  getContentLanguage,
  getSavedCurrentPage,
  getSavedRowsPerPage,
  Group,
  handleActivateItems,
  handleDeactivateItems,
  handleDeleteItems,
  handleDuplicateItems,
  languageSchemaRequired,
  languageSortComparator,
  onSelectAll,
  onSelectRow,
  useGroup,
  useGroups,
} from '@/core';
import { DefaultCell, ImageCell, IsActiveStatusCell } from '@/core/renderCells';
import {
  OfferHeader,
  OfferModal,
  PageHeader,
  prepareProductGroupToPost,
  TOfferType,
} from '@/features';
import { useImage } from '@/features/product/hooks';

const schema = z.object({
  name: languageSchemaRequired,
  showImmediately: z.boolean(),
  image: z.number().optional(),
  oldImageId: z.number().optional(),
});

const OFFER_PAGE_KEY = 'offers';

export const PageMainOffer = () => {
  const { t } = useTranslation();

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [actionType, setActionType] = useState<'EDIT' | 'ADD'>('EDIT');
  const [selectedRowToEdit, setSelectedRowToEdit] = useState<Group | undefined>(undefined);

  //pagination states
  const [currentPage, setCurrentPage] = useState<number>(getSavedCurrentPage(OFFER_PAGE_KEY));
  const [rowsPerPage, setRowsPerPage] = useState<number>(getSavedRowsPerPage(OFFER_PAGE_KEY));

  const [isNewImageUploadInProgress, setIsNewImageUploadInProgress] = useState(false);

  const methods = useForm<TOfferType>({
    defaultValues: {
      name: { en: '', cs: '', de: '' },
      showImmediately: false,
      image: undefined,
      oldImageId: undefined,
    },
    resolver: zodResolver(schema),
  });

  const {
    handleSubmit,
    formState: { errors },
  } = methods;

  const noErrors = !Object.values(errors).some((error) => error);

  const [searchInputValue, setSearchInputValue] = useState<string>('');
  const debouncedSearchInputValue = useDebounce(searchInputValue, DEBOUNCE_TIME_MS);

  const {
    useGroupsQuery: { data, status, refetch },
    useGroupsSearch,
  } = useGroups({ page: currentPage, limit: rowsPerPage });
  const {
    mutations: {
      createMutation,
      updateMutation,
      deleteMutation,
      duplicateMutation,
      deactivateMutation,
      activateMutation,
      reorderMutation,
    },
  } = useGroup();

  const {
    mutations: { deleteImageMutation },
  } = useImage();

  const { data: groupsFilteredByName, status: groupsFilteredByNameStatus } =
    useGroupsSearch(debouncedSearchInputValue);

  const isFilterActive = debouncedSearchInputValue.length > 0;
  const displayRows = isFilterActive ? groupsFilteredByName?.results || [] : data?.results || [];
  const displayTotal = isFilterActive
    ? groupsFilteredByName?.total_results || 0
    : data?.total_results || 0;
  const paginationTotal = isFilterActive
    ? groupsFilteredByName?.total_results || (debouncedSearchInputValue ? 1 : 0)
    : data?.total_results || 0;

  const handleChangeModalState = (state: boolean) => {
    if (!state) {
      methods.reset();
    }
    setIsModalOpen(state);
  };

  const handleCancelAction = () => {
    setSelectedRows([]);
  };

  const onRowEdit = (selectedRowId: number) => {
    if (!data) return;
    const selectedRow = findRowById(data.results, selectedRowId);
    if (!selectedRow) return;
    methods.setValue('name', selectedRow.name);
    methods.setValue('showImmediately', selectedRow.is_active);
    methods.setValue('image', selectedRow.image_id);
    setActionType('EDIT');
    setSelectedRowToEdit(selectedRow);
    handleChangeModalState(true);
  };

  const onHeaderButtonClick = () => {
    methods.reset();
    setActionType('ADD');
    setSelectedRowToEdit(undefined);
    handleChangeModalState(true);
  };

  const getRowById = (rowId?: number) => {
    if (!data || !rowId) return;
    return findRowById(data.results, rowId);
  };

  const onSubmit = async (data: TOfferType) => {
    if (actionType === 'ADD') {
      await createMutation.mutateAsync(
        prepareProductGroupToPost({
          name: data.name,
          image: data.image,
          showImmediately: data.showImmediately,
        }),
      );
    } else {
      if (!selectedRowToEdit) return;
      await updateMutation.mutateAsync(
        {
          id: selectedRowToEdit.id,
          ...prepareProductGroupToPost({
            name: data.name,
            image: data.image,
            showImmediately: data.showImmediately,
          }),
        },
        {
          onSuccess: async () => {
            if (data.oldImageId) {
              await deleteImageMutation.mutateAsync(data.oldImageId);
            }
          },
        },
      );
    }
    handleChangeModalState(false);
    methods.reset();
    void refetch();
  };

  const handleDeleteProductGroups = async () => {
    await handleDeleteItems({
      selectedRows,
      setSelectedRows,
      refetch,
      deleteMutation,
    });
  };

  const handleDeactivateProductGroups = async () => {
    await handleDeactivateItems({
      selectedRows,
      setSelectedRows,
      refetch,
      deactivateMutation,
    });
    setSelectedRowToEdit(undefined);
  };

  const handleActivateProductGroups = async () => {
    await handleActivateItems({
      selectedRows,
      setSelectedRows,
      refetch,
      activateMutation,
    });
    setSelectedRowToEdit(undefined);
  };

  const handleDragEnd = async (reorderedRows: Group[], draggedRowId: number) => {
    const newIndex = reorderedRows.findIndex((row) => row.id === draggedRowId);
    if (newIndex === -1) return;
    await reorderMutation.mutateAsync({ id: draggedRowId, position: newIndex });
  };

  const handleDuplicateProductGroups = async () => {
    await handleDuplicateItems({
      selectedRows,
      setSelectedRows,
      refetch,
      duplicateMutation,
    });
    setSelectedRowToEdit(undefined);
  };

  const columns: GridColDef[] = [
    {
      field: 'image_id',
      width: 60,
      headerName: '',
      renderCell: (params) => <ImageCell imageId={params.value} />,
    },
    {
      field: 'name',
      headerName: t('HEADERS.NAME'),
      sortComparator: languageSortComparator,
      flex: 0.2,
      renderCell: (params) => (
        <DefaultCell value={params.value[getContentLanguage(i18n.language)]} />
      ),
    },
    {
      field: 'is_active',
      flex: 1,
      headerName: t('HEADERS.STATUS'),
      renderCell: (params) => <IsActiveStatusCell status={params.value} />,
    },
  ];
  return (
    <Stack>
      <PageHeader
        onButtonClick={onHeaderButtonClick}
        actionButtonLabel={t('DIALOG_TITLE.CATEGORIES.ADD')}
      />
      <RelativelyPositionedSearchFilter
        inputValue={searchInputValue}
        setInputValue={setSearchInputValue}
      />
      <Table
        key={`${searchInputValue}_${currentPage}_${rowsPerPage}_${JSON.stringify(data)}_${JSON.stringify(groupsFilteredByName)}`}
        rows={displayRows}
        tableId={OFFER_PAGE_KEY}
        columns={columns}
        isDraggable={true}
        onDragEnd={handleDragEnd}
        isCheckBox={true}
        isRowsEditable={true}
        loading={
          status === 'pending' ||
          createMutation.status === 'pending' ||
          updateMutation.status === 'pending' ||
          deleteMutation.status === 'pending' ||
          groupsFilteredByNameStatus === 'pending'
        }
        onEditRow={onRowEdit}
        selectedRows={selectedRows}
        onSelectAll={() => onSelectAll(data, selectedRows, setSelectedRows)}
        onSelectRow={(selectedRowId) => onSelectRow(selectedRowId, setSelectedRows)}
        pagination={true}
        paginationOptions={{
          currentPage,
          rowsPerPage,
          totalRows: paginationTotal,
          rowCount: paginationTotal,
          rowsPerPageOptions: [15, 25, 50, 100],
          onPageSizeChange: (newPageSize: number) => {
            setCurrentPage(0);
            setRowsPerPage(newPageSize);
          },
          onPageChange: (newPage: number) => {
            setCurrentPage(newPage);
          },
          pageSize: rowsPerPage,
          page: currentPage,
        }}
        ToolBar={(props) => (
          <OfferHeader
            {...props}
            count={displayTotal}
            selectedRows={selectedRows}
            onCancel={handleCancelAction}
            onDuplicate={handleDuplicateProductGroups}
            onDeactivate={handleDeactivateProductGroups}
            onActivate={handleActivateProductGroups}
            onDelete={handleDeleteProductGroups}
          />
        )}
      />
      <FormProvider {...methods}>
        <DialogStyled
          open={isModalOpen}
          title={
            actionType === 'ADD'
              ? t('DIALOG_TITLE.CATEGORIES.ADD')
              : t('DIALOG_TITLE.CATEGORIES.CHANGE')
          }
          sx={{ '&& .MuiPaper-root': { width: '100%', maxWidth: '500px' } }}
          setOpen={handleChangeModalState}
          onActionButtonClick={handleSubmit(onSubmit)}
          actionButtonLabel={actionType === 'ADD' ? t('ACTIONS.ADD') : t('ACTIONS.EDIT')}
          isCorrectlyFilledOut={noErrors}
          isLoading={
            createMutation.status === 'pending' ||
            updateMutation.status === 'pending' ||
            isNewImageUploadInProgress
          }
        >
          <OfferModal
            imageId={getRowById(selectedRowToEdit?.id)?.image_id || undefined}
            setIsNewImageUploadInProgress={setIsNewImageUploadInProgress}
          />
        </DialogStyled>
      </FormProvider>
    </Stack>
  );
};
